import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-form';
import * as queryString from 'query-string';
import { parsePhoneNumber } from 'react-phone-number-input';
import { cloneDeep } from 'lodash';
import NavigationPrompt from 'react-router-navigation-prompt';
import ModalVideo from 'react-modal-video';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../components/Modal/ReactBootstrapModal';

import { getEvent, updateEvent } from '../../actions/event-actions';
import * as clubActions from '../../actions/club-actions';
import * as splashActions from '../../actions/splash-actions';

import Step from '../../components/Events/EventEditWizard/Step';
import StepFields from '../../components/Events/EventEditWizard/StepFields';
import { IncompleteApplicationPopup, EventDuplicatePopup } from '../../components/PopUps';
import requireAuth from '../../components/hocs/requireAuth';
import Img from '../../components/Img';
import UpgradeToPerformanceHsPopUp from '../../components/Upgrade/UpgradeToPerformanceHs/UpgradeToPerformanceHsPopUp';
import UpgradeToProClubPopUp from '../../components/Upgrade/UpgradeToProClubPopUp';

import { payments } from '../../../constants';
import { determineStepTitle } from '../../utils/stepTitles';
import { eventCreateSteps, getEventDivisionAgeKeyByValue } from '../../utils/constants';
import { getDefaultDivision, PopUpTypes, mapDivisionsForSave, shouldCheckForEventDuration, getEventDuration, getMaxEventDuration, getAllowedEventTypes, EventTypes } from '../../utils/events';
import { getApiAxiosInstance } from '../../utils/network';
import { setupStripeAccount } from '../../api/clubsApi';
import { getNewClubPath, isClubOwner } from '../../utils/clubs';
import { passwordForDisplay, verifiedAcceptanceMinValue, directorRoleIds } from '../../utils/eventConstants';
import { sumBy } from '../../utils';
import { formatDefaultValues } from '../../utils/eventEdit';
import { useStatsig } from '../../components/hooks/useStatsig';
import { showErrorPopup } from '../../actions/notification-actions';
import PopupV3, { PopupTypes } from '../../components/Popup/PopupV3';
import RecurringSubmissionPopups, { initializeRecurringPublishSelection } from '../../components/Events/EventEditWizard/RecurringSubmissionPopups';
import StatsigLink from '../../components/Links/StatsigLink';

import config from '../../../config';
import { routerLocationShape, routerHistoryShape, routerMatchShape } from '../../propTypes';
import styles from './eventEdit.scss';

const _ = { cloneDeep };

const remainingTiersDefault = [
    { displayOrder: 10, shortName: ' ', sponsors: [] },
    { displayOrder: 9, shortName: ' ', sponsors: [] },
    { displayOrder: 8, shortName: ' ', sponsors: [] },
    { displayOrder: 7, shortName: ' ', sponsors: [] },
    { displayOrder: 6, shortName: ' ', sponsors: [] },
    { displayOrder: 5, shortName: ' ', sponsors: [] },
    { displayOrder: 4, shortName: ' Bronze Sponsors', sponsors: [] },
    { displayOrder: 3, shortName: ' Silver Sponsors', sponsors: [] },
    { displayOrder: 2, shortName: ' Gold Sponsors', sponsors: [] },
    { displayOrder: 1, shortName: ' Platinum Sponsors', sponsors: [] },
];
function EventEditPageV2(props) {
    const { location: { search: queryParams } } = props;
    const { match, history, location } = props;
    const { eventId } = match.params;
    const [tiers, setTiers] = useState({ tiers: [] });
    const [remainingTiers, setRemainingTiers] = useState([]);

    const { step: stepParam, cloned } = queryString.parse(queryParams);
    const formApi = useRef();
    const eventFacilitiesHydratedRef = useRef(false); // Hydration ref for eventFacilities bridge
    const publishIntentRef = useRef(false); // tracks publish intent without closure-staleness issues
    const { checkGate, featureGates } = useStatsig();
    const isClubRedirectEnabled = checkGate(featureGates.CLUB_LINKS_REDIRECT);
    const [state, setState] = useState({
        totalSteps: 4,
        step: stepParam ? parseInt(stepParam) : 1,
        submitting: false,
        event: null,
        popUpMessage: '',
        popUpType: null,
        topPoint: null,
        showCloneEventPopUp: cloned || false,
        hasAddEventSponsors: true,
        eventDataModified: false,
        errorPopUp: false,
        showUpgradeToPerformanceHsPopup: false,
        clubToUpgrade: null,
        showProClubPopup: false,
        tmsEventSponsors: {},
        showRecurringSaveDraftPopup: false,
        showRecurringPublishPopup: false,
        recurringPublishSelection: [],
        publish: false,
    });

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            totalSteps: eventCreateSteps.STEPS_5,
        }));
    }, []);
    const { event, step, submitting, showCloneEventPopUp } = state;
    const dispatch = useDispatch();

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const club = useSelector(({ club }) => club);

    const { clubDetails, clubId } = club;

    const isOwner = isClubOwner(clubDetails);

    let { paymentProvider } = payments;
    if (clubDetails?.paymentProviderSetupType?.value) {
        paymentProvider = clubDetails.paymentProviderSetupType.value;
    }
    const activePaymentsEnabled = clubDetails?.activePaymentProviderTypes?.find(payment => payment.value === paymentProvider);
    const paymentSetupComplete = clubDetails?.paymentsEnabled && activePaymentsEnabled;

    const stripeUrl =
        `${config.stripeHost}/oauth/authorize?client_id=${config.stripeClientId}` +
        `&state=${encodeURI(JSON.stringify({ eventId }))}` +
        '&response_type=code' +
        '&scope=read_write';

    // PAGE INIT

    // page init - load event, and populate default values if clubDetails are already in state
    const init = async () => {
        const { eventId } = match.params;
        dispatch(splashActions.showSplash({
            splash: 'load',
            title: 'Loading',
            description: 'Please wait while we\'re loading your event',
        }));
        let event = await dispatch(getEvent(eventId, true, false));
        if (event.error) {
            if (event.redirectUrl) {
                history.push(event.redirectUrl);
            } else {
                history.push('/');
            }
        }
        if (!currentMemberIsAdmin(event)) {
            window.location.href = `/events/${eventId}`;
            return;
        }

        // load club if needed, otherwise populate event if needed
        if (club.clubId !== event.club.id) {
            dispatch(clubActions.setClubId(event.club.id));
        } else if (!event.eventDivisions?.length || !event.eventLocations?.length) {
            const eventUpdate = populateDefaultValues(event, clubDetails);
            event = { ...event, ...eventUpdate };
        }
        const stateData = formatEvent(event);
        const alreadyAddedTiersIds = event?.tmsEventSponsors?.sponsorTiers.map(tier => tier.displayOrder);
        const alreadyAddedTiers = new Set([...alreadyAddedTiersIds]);
        const temp = [];
        remainingTiersDefault.forEach(tier => {
            if (!alreadyAddedTiers.has(tier.displayOrder)) {
                temp.push(tier);
            }
        });
        setRemainingTiers([...temp]);
        setTiers({
            tiers: [...event?.tmsEventSponsors?.sponsorTiers || {}],
        });
        setState((prevState) => ({
            ...prevState,
            ...stateData,
            redirectUri: `${config.appHost}/events/${eventId}/edit`,
        }));
    };

    useEffect(() => {
        window.addEventListener('beforeunload', onUnload);
        init();

        return () => {
            window.removeEventListener('beforeunload', onUnload);
        };
    }, []);

    const populateDefaultValues = (event, clubDetails) => {
        const eventUpdate = {};
        if (clubDetails && event?.eventType) {
            if (!event.eventDivisions?.length) {
                const currencyId = event.currency ? event.currency.id : clubDetails.currency.id;
                const defaultDivision = getDefaultDivision(event, currencyId, true);
                eventUpdate.eventDivisions = [defaultDivision];
            }
            if (!event.eventLocations?.length) {
                const defaultLocation = {
                    location: (clubDetails?.location.googleFormattedName) || '',
                    eventLocationType: '1',
                };
                eventUpdate.eventLocations = [defaultLocation];
            }
            return eventUpdate;
        }
    };

    useEffect(() => {
        // populate default values if needed
        if (event && clubDetails && clubId === event.club?.id && (!event.eventDivisions?.length || !event.eventLocations?.length)) {
            const eventUpdate = populateDefaultValues(event, clubDetails);
            setState((prevState) => ({
                ...prevState,
                event: {
                    ...event,
                    ...eventUpdate,
                },
            }));
            if (clubDetails && event && !state.loading && !state.isEventUpdating) {
                dispatch(splashActions.hideSplash());
            }
        }
    }, [!!clubDetails, clubId]);

    const setupPaymentAccount = async () => {
        // show linking splash
        setState((prevState) => ({
            ...prevState,
            loading: true,
        }));
        dispatch(splashActions.showSplash({
            splash: 'load',
            title: 'Linking account',
            description: 'Please wait while we\'re linking your payment account',
        }));
        try {
            await setupStripeAccount(getApiAxiosInstance(), clubId, {
                code: queryParams.code,
                redirectUri: `${state.redirectUri}`,
            });
            dispatch(clubActions.setClubId(clubId));
            setState((prevState) => ({
                ...prevState,
                loading: false,
                success: true,
                totalSteps: eventCreateSteps.STEPS_6,
                step: 6,
            }));
            dispatch(splashActions.hideSplash());
        } catch (error) {
            setState((prevState) => ({
                ...prevState,
                loading: false,
                error: error.response?.data?.DisplayMessage || 'error',
                totalSteps: eventCreateSteps.STEPS_6,
                step: 6,
            }));
            dispatch(splashActions.hideSplash());
        }
    };

    useEffect(() => {
        if (clubDetails && event && !state.loading && !state.isEventUpdating) {
            dispatch(splashActions.hideSplash());
        }
        if (clubDetails && !paymentSetupComplete) {
            const queryParams = queryString.parse(location.search);
            if (queryParams.code && !state.loading && !state.success && !state.error && !state.errorPopUp) {
                // setup Stripe account
                setupPaymentAccount();
            }
        }
    }, [!!clubDetails, paymentSetupComplete]);

    const onDivisionFeeChange = (value) => {
        setState((prevState) => ({
            ...prevState,
            tips: { ...prevState.tips, entryFee: !value },
        }));
    };

    const onUnload = (event) => {
        // browser api handles the warning
        if (unsavedChanges()) {
            event.returnValue = 'Leave page? Changes you made may not be saved.';
        }
    };

    const onVerifiedChange = (value) => {
        checkAndUpdateEventDivisions();
        setState((prevState) => ({
            ...prevState,
            tips: { ...prevState.tips, verifiedEvent: !value },
        }));
    };

    const getStepTitles = (currentStep, clubDetails) => {
        const isOwner = isClubOwner(clubDetails);

        const { stepTitle, stepSubTitle } = determineStepTitle(currentStep, activePaymentsEnabled, clubDetails, isOwner);
        return { stepTitle, stepSubTitle };
    };

    const setEventDataModified = () => {
        setState((prevState) => ({
            ...prevState,
            eventDataModified: true,
        }));
        if (formApi.current?.errors && state.submitting) {
            setState((prevState) => ({
                ...prevState,
                submitting: false,
                publish: false,
            }));
        }
    };

    const setOrganizers = (organizers) => {
        setState((prevState) => ({
            ...prevState,
            organizers,
        }));
    };

    const isNotTeamTennisOrDualMatch = () => {
        const eventTypeOptions = clubDetails ? getAllowedEventTypes({
            club: clubDetails,
            sportTypeId: event?.sportTypeId,
        }) : [];

        return eventTypeOptions.some(type => type.id !== EventTypes.DUAL_MATCH && type.id !== EventTypes.TEAM_MATCH) &&
            formApi.current?.values?.eventType !== EventTypes.DUAL_MATCH &&
            formApi.current?.values?.eventType !== EventTypes.TEAM_MATCH;
    };

    const applyForVerified = async () => {
        await dispatch(clubActions.applyForVerified(club.clubId));
    };

    const checkAndUpdateEventDivisions = () => {
        if (formApi.current) {
            const values = _.cloneDeep(formApi.current.values);
            // update values for divisions after updating to verified events
            const eventDivisions = values.eventDivisions.map((division) => {
                // default to officilas for a verified event
                division.officialsType = '2';
                // change max players to min 4 if chosen less than 4 before making it verified
                division.maxPlayers = parseInt(division.maxPlayers) < verifiedAcceptanceMinValue ? verifiedAcceptanceMinValue : division.maxPlayers;
                return division;
            });
            formApi.current.setValue('eventDivisions', eventDivisions);
        }
    };

    const closeEditPage = () => {
        history.push(`/events/${eventId}`);
    };

    const closePopups = () => {
        setState((prevState) => ({
            ...prevState,
            showIncompletePopup: false,
        }));
    };

    const currentMemberIsAdmin = (event) => {
        const eventDirectorRole = event?.currentMember?.eventDirectorRole;
        const clubMemberRole = event?.currentMember?.clubMemberRole;

        return (eventDirectorRole && (eventDirectorRole.value === 'owner' || eventDirectorRole.value === 'admin')) ||
            (clubMemberRole && (clubMemberRole.value === 'Owner' || clubMemberRole.value === 'Admin'));
    };

    const exposeFormApi = (exposedFormApi) => {
        if (exposedFormApi) {
            formApi.current = exposedFormApi;
        }
    };

    const formatEvent = (event) => {
        let organizers = event.eventDirectors;
        organizers = organizers.map(organizer => {
            organizer.imgUrl = '/img/default-profile-icon-120-x-120.svg';
            let title;
            if (organizer.eventDirectorRole === directorRoleIds.OWNER) {
                title = 'Head Provider';
            } else if (organizer.eventDirectorRole === directorRoleIds.VOLUNTEER) {
                title = 'Event Volunteer';
            } else {
                title = 'Event Provider';
            }
            organizer.title = title;
            return organizer;
        });

        const location = event.eventLocations[0];
        let locationStr = location?.googleFormattedName;
        if (location?.placeName) {
            locationStr = `${location.placeName}, ${locationStr}`;
        }

        event.eventSchedule.eventStartUtc = `${event.eventSchedule.eventStartUtc}Z`;
        event.eventSchedule.eventEndUtc = `${event.eventSchedule.eventEndUtc}Z`;
        event.eventSchedule.registrationStartUtc = `${event.eventSchedule.registrationStartUtc}Z`;
        event.eventSchedule.registrationEndUtc = `${event.eventSchedule.registrationEndUtc}Z`;
        if (event.eventSchedule.drawPublishedUtc) {
            event.eventSchedule.drawPublishedUtc = `${event.eventSchedule.drawPublishedUtc}Z`;
        }

        // convert age ranges back to keys to populate slider
        event.eventDivisions = event.eventDivisions.map(div => {
            div.ageRange = [parseInt(getEventDivisionAgeKeyByValue(div.ageRange[0])), parseInt(getEventDivisionAgeKeyByValue(div.ageRange[1]))];
            return div;
        });

        // map selects to toggles
        event.$verified = event.utrType.toString();
        event.$clubJoin = event.eventClubJoinRequestType === 2;

        return {
            organizers,
            event,
            location: locationStr,
        };
    };

    const handleStepBack = () => {
        setState((prevState) => ({
            ...prevState,
            step: prevState.step - 1,
        }));
    };

    const applyPublishSelectionToRecurrenceSettings = (recurrenceSettings, publishSelection = state.recurringPublishSelection) => {
        if (!recurrenceSettings?.cadenceId || !publishSelection?.length) {
            return recurrenceSettings;
        }
        const publishSelectionByKey = new Map();
        publishSelection.forEach((recurrentEvent, index) => {
            const rowKey = recurrentEvent.id || recurrentEvent.order || index + 1;
            publishSelectionByKey.set(rowKey, recurrentEvent);
        });
        return {
            ...recurrenceSettings,
            recurrentEvents: (recurrenceSettings.recurrentEvents || []).map((recurrentEvent, index) => {
                const rowKey = recurrentEvent.id || recurrentEvent.order || index + 1;
                const selectedEvent = publishSelectionByKey.get(rowKey);
                return {
                    ...recurrentEvent,
                    publishOnCreate: typeof selectedEvent?.publishOnCreate === 'boolean'
                        ? selectedEvent.publishOnCreate
                        : !!recurrentEvent.publishOnCreate,
                };
            }),
        };
    };

    const handleSubmit = async (newValues, toStep) => {
        const updated = {
            ...event,
            club: club.clubDetails.id, // handles edge case for coming back from wepay
            ...(newValues ? newValues.values : {}),
        };
        const wasPublished = !!event?.published;
        const willBePublished = !!state.publish || publishIntentRef.current;
        publishIntentRef.current = false;

        updated.published = wasPublished || willBePublished;

        // only mark as "just published" if transitioning false → true
        if (!wasPublished && willBePublished) {
            updated.$justPublished = true;
        }
        updated.recurrenceSettings = applyPublishSelectionToRecurrenceSettings(updated.recurrenceSettings);

        updated.tmsEventSponsors = {
            sponsorTiers: tiers?.tiers.map((tier) => ({
                shortName: tier.shortName,
                displayOrder: tier.displayOrder,
                sponsors: tier?.sponsors?.map(sponsor => ({
                    sponsorName: sponsor.sponsorName,
                    callToActionURL: sponsor.callToActionURL,
                    imageFile: sponsor.imageFile,
                    displayOrder: sponsor.displayOrder,
                })),
            })),
        };
        updated.hasAddEventSponsors = true;

        const clubLocation = clubDetails?.location || {};
        const isEventInUnitedStates = (clubLocation.countryName === 'United States' || clubLocation.countryCode3 === 'USA' || clubLocation.countryIoc === 'USA' || clubLocation.countryCode2 === 'US');
        const isEventPaid = parseInt(updated.entryFee) > 0 || parseInt(updated.eventDivisions?.reduce((total, division) => total + (division.entryFee || 0), 0)) > 0;
        if (checkGate(featureGates.EVENTDESK_NOTIFICATIONS) && isEventInUnitedStates && !isEventPaid) {
            updated.enableEventDeskNotifications = false;
        }

        if (toStep) {
            handleToStep(toStep);
        } else {
            if (shouldCheckForEventDuration(updated, updated.eventSchedule)) {
                if (getEventDuration(updated.eventSchedule) >= getMaxEventDuration(clubDetails.clubSettings)) {
                    dispatch(showErrorPopup({
                        message: `The event end date can not be more than ${getMaxEventDuration(clubDetails.clubSettings)} days apart from event start date`,
                    }));

                    setState((prevState) => ({
                        ...prevState,
                        submitting: false,
                    }));

                    return;
                }
            }

            const vals = _.cloneDeep(updated); // state getting updated (event division acceptance type to WAIT_LIST / CAPPED_REGISTRATION), to avoid cloning the object
            vals.name = vals.name.trim();
            vals.eventDirectors = state.organizers.map(admin => admin);
            if (vals.phone) {
                const parsedPhoneNumber = parsePhoneNumber(vals.phone);
                if (parsedPhoneNumber) {
                    vals.phoneCountryCode = parsedPhoneNumber.country;
                }
            }
            const { acceptanceListRankingTypes, acceptanceListRoundTypes } = coreUI.types.tms.division.tournament;
            const useAcceptanceList = true;
            vals.eventDivisions = mapDivisionsForSave(vals.eventDivisions, acceptanceListRankingTypes, acceptanceListRoundTypes, useAcceptanceList);

            vals.eventLocations = vals.eventLocations.map(location => {
                if (!location.eventLocationType) {
                    location.eventLocationType = 1;
                }
                return location;
            });

            delete vals.draws;

            if (vals.passwordEnabled && vals.eventPassword === passwordForDisplay) {
                vals.eventPassword = '';
            }

            // map toggles to type selects
            vals.utrType = vals.$verified;
            vals.eventClubJoinRequestType = vals.$clubJoin ? 2 : 1;

            if (vals.eventQuestions) {
                vals.eventQuestions = vals.eventQuestions.filter(question => {
                    return question?.text;
                });
            }

            if (state.publish && vals?.recurrenceSettings?.cadenceId) {
                vals.recurrenceSettings = applyPublishSelectionToRecurrenceSettings(vals.recurrenceSettings);
            }
            vals.recurrenceSettings = vals?.recurrenceSettings?.cadenceId ? vals.recurrenceSettings : null;

            setState((prevState) => ({
                ...prevState,
                submitting: true,
                isEventUpdating: true,
            }));
            dispatch(splashActions.showSplash({
                splash: 'save',
                title: 'Saving',
            }));
            const updatedEvent = await dispatch(updateEvent(vals));
            setState((prevState) => ({
                ...prevState,
                isEventUpdating: false,
            }));
            dispatch(splashActions.hideSplash());
            setState((prevState) => ({
                ...prevState,
                submitting: false,
            }));
            if (updatedEvent) {
                setState((prevState) => ({
                    ...prevState,
                    eventDataModified: false,
                }));

                const splashText = !event.published && updatedEvent.published
                    ? 'Your Event has been Published!'
                    : 'Your Event has been Updated!';
                history.push({
                    pathname: `/events/${updatedEvent.id}`,
                    state: { adminSplash: splashText },
                });
            }
        }

        if (window) {
            window.scrollTo(0, 0);
        }
    };

    const openModal = (video) => {
        setState((prevState) => ({
            ...prevState,
            isOpen: video,
        }));
    };

    const submitPublishEvent = (publishSelection = null) => {
        const event = _.cloneDeep(formApi.current?.values);
        if (publishSelection?.length && event?.recurrenceSettings?.cadenceId) {
            event.recurrenceSettings = applyPublishSelectionToRecurrenceSettings(event.recurrenceSettings, publishSelection);
            if (formApi.current) {
                formApi.current.setValue('recurrenceSettings.recurrentEvents', event.recurrenceSettings.recurrentEvents);
            }
        }
        const clubIsSanctioned = clubDetails ? clubDetails.sanctioned : false;
        const paymentsEnabledForClub = clubDetails ? clubDetails.paymentsEnabled : false;
        const eventDivisionEntryFeeSum = event?.eventDivisions ? sumBy(event.eventDivisions, (division) => { return division.entryFee ? parseInt(division.entryFee) : 0; }) : 0;
        const eventAllowOffPlatformPayments = event ? event.allowOffPlatformPayments : false;
        if (!(event?.eventDivisions?.length)) {
            dispatch(showErrorPopup({
                message: 'Unable to publish event. Please add at least one Event Division.',
            }));
        } else if (!(event?.eventLocations?.length)) {
            dispatch(showErrorPopup({
                message: 'Unable to publish event. Please add at least one Event Location.',
            }));
        } else if ((event.$verified === '2') && !clubIsSanctioned) {
            showIncompletePopup();
        } else if (!paymentsEnabledForClub && eventDivisionEntryFeeSum > 0 && !eventAllowOffPlatformPayments) {
            dispatch(showErrorPopup({
                message: 'Unable to publish event. An active club payment account is required.',
            }));
        } else if (!state.submitting) {
            setState((prevState) => ({
                ...prevState,
                submitting: true,
            }));
            if (formApi.current) {
                formApi.current.submitForm();
            }
        }
    };

    const publishEvent = () => {
        // use formApi values to check event division costs and $verified because the fields can change and immediately be saved instead of going to state values.
        const event = formApi.current?.values;
        const recurrenceSettings = event?.recurrenceSettings;
        if (!event?.published && recurrenceSettings?.cadenceId) {
            const recurrentEvents = recurrenceSettings?.recurrentEvents || [];
            const recurringPublishSelection = initializeRecurringPublishSelection(recurrentEvents);
            setState((prevState) => ({
                ...prevState,
                showRecurringPublishPopup: true,
                recurringPublishSelection,
            }));
            return;
        }
        // Non-recurring: always publish. Use a ref so handleSubmit sees it immediately
        // (setState is async and the onSubmit closure captures a stale state snapshot).
        publishIntentRef.current = true;
        setState((prevState) => ({ ...prevState, publish: true }));
        submitPublishEvent();
    };

    const saveDraftEvent = () => {
        const recurrenceSettings = formApi.current?.values?.recurrenceSettings;
        if (!event?.published && recurrenceSettings?.cadenceId) {
            setState((prevState) => ({
                ...prevState,
                showRecurringSaveDraftPopup: true,
            }));
            return;
        }
        if (!state.submitting) {
            setState((prevState) => ({
                ...prevState,
                submitting: true,
            }));
            if (formApi.current) {
                formApi.current.submitForm();
            }
        }
    };

    const closeRecurringSaveDraftPopup = () => {
        setState((prevState) => ({
            ...prevState,
            showRecurringSaveDraftPopup: false,
        }));
    };

    const closeRecurringPublishPopup = () => {
        setState((prevState) => ({
            ...prevState,
            showRecurringPublishPopup: false,
        }));
    };

    const confirmRecurringSaveDraft = () => {
        closeRecurringSaveDraftPopup();
        if (!state.submitting) {
            setState((prevState) => ({
                ...prevState,
                submitting: true,
            }));
            if (formApi.current) {
                formApi.current.submitForm();
            }
        }
    };

    const updateRecurringPublishSelection = (key, publishOnCreate) => {
        setState((prevState) => ({
            ...prevState,
            recurringPublishSelection: prevState.recurringPublishSelection.map((recurrentEvent, index) => {
                const rowKey = recurrentEvent.id || recurrentEvent.order || index + 1;
                if (rowKey !== key) {
                    return recurrentEvent;
                }
                return {
                    ...recurrentEvent,
                    publishOnCreate,
                };
            }),
        }));
    };

    const toggleSelectAllRecurringPublishSelection = (nextValue) => {
        setState((prevState) => ({
            ...prevState,
            recurringPublishSelection: prevState.recurringPublishSelection.map((recurrentEvent) => ({
                ...recurrentEvent,
                publishOnCreate: nextValue,
            })),
        }));
    };

    const confirmRecurringPublish = () => {
        closeRecurringPublishPopup();
        publishIntentRef.current = true;
        setState((prevState) => ({ ...prevState, publish: true }));
        submitPublishEvent(state.recurringPublishSelection);
    };

    const showIncompletePopup = () => {
        setState((prevState) => ({
            ...prevState,
            showIncompletePopup: true,
        }));
    };

    const handleToStep = (direction) => {
        setState((prevState) => ({
            ...prevState,
            step: direction === 'prev' ? (prevState.step - 1) : (prevState.step + 1),
        }));
    };

    const unsavedChanges = () => {
        return !state.overrideClosePageWarning && state.eventDataModified;
    };

    const showUpgradeToPerformanceHsPopup = () => {
        const { name } = clubDetails;
        setState((prevState) => ({
            ...prevState,
            showUpgradeToPerformanceHsPopup: true,
            clubToUpgrade: [{
                value: clubId,
                label: name,
            }],
        }));
    };

    const handlePerformanceHsPopupCloseClick = () => {
        setState((prevState) => ({
            ...prevState,
            showUpgradeToPerformanceHsPopup: false,
            clubToUpgrade: null,
        }));
    };

    const handlePerformanceHsPopupUpgradeClick = () => {
        setState({
            showUpgradeToPerformanceHsPopup: false,
            clubToUpgrade: null,
            showProClubPopup: true,
        });
    };

    const handleProClubSuccess = () => {
        setState((prevState) => ({
            ...prevState,
            overrideClosePageWarning: true,
        }));
        const clubTierTypeId = clubDetails.tierTypeId || clubDetails.tierType?.id;
        const newClubPath = getNewClubPath(clubTierTypeId);
        if (clubId) {
            if (isClubRedirectEnabled) {
                dispatch(splashActions.showSplash());
                window.location.href = `${config.nextAppHost}/${newClubPath}/${clubId}`;
                return;
            }
            history.push(`/clubs/${clubId}`);
        } else {
            history.push(`/home`);
        }
    };

    // render

    if (event) {
        // prepened location place name
        event.eventLocations.forEach(element => {
            if (element.placeName) {
                element.location = `${element.placeName}, ${element.display}`;
            }
        });
    }

    const defaultValues = formatDefaultValues(event, clubDetails, coreUI);
    const showStepHeader = isNotTeamTennisOrDualMatch();

    let eventStatus = null;
    if (submitting) {
        eventStatus = event?.published ? 'Updating ...' : 'Publishing ...';
    } else {
        eventStatus = event?.published ? 'Update' : 'Publish';
    }

    const handleClose = () => {
        setState((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    return (
        <div>
            <ModalVideo channel="youtube" isOpen={state.isOpen === 'video2'} videoId={'1efJOfGhzaY'} autoplay={true} onClose={handleClose} />
            <div className="navbar navbar-expand-md navbar-light mt0 sticky-top">
                <div className="container" style={{ position: 'relative' }}>
                    <div className="row w-100">
                        <div className="col d-flex aic">
                            <div className="close-area" onClick={closeEditPage} data-testid="event-edit.close-btn">
                                <Img className="close-icon" src="/img/icons/close-icon-lg.svg" />
                            </div>
                            <div className="d-inline d-md-none title-span text-medium text-uppercase text-dark text-center" data-testid="event-edit.header.mobile-title">
                                Edit Event
                            </div>
                        </div>
                        <div className="col d-none d-md-flex aic jcc">
                            <div className="title-span text-medium text-uppercase text-dark text-center" data-testid="event-edit.header.desktop-title">
                                Edit Event
                            </div>
                        </div>
                        <div className="col d-flex aic jce">
                            {event && !event.published && !submitting &&
                                <><span className="clickable" onClick={saveDraftEvent} data-testid="event-edit.save-draft-btn">Save Draft&nbsp;</span><span className="d-md-none">&nbsp;</span></>}
                            {event && !event.published && submitting &&
                                <>Save Draft&nbsp;<span className="d-md-none">&nbsp;</span></>}
                            {event && !submitting &&
                                <span className="clickable d-md-none" onClick={publishEvent} data-testid="event-edit.publish-btn">{event.published ? 'Update' : 'Publish'}</span>}
                            {event && submitting &&
                                <span className="d-md-none" data-testid="event-edit.header.mobile-publish-status">{event.published ? 'Updating...' : 'Publishing...'}</span>}
                            <button
                                type="button"
                                className="btn btn-primary btn-lg text-uppercase ml16 d-none d-md-inline-block"
                                onClick={publishEvent}
                                disabled={submitting}
                                data-testid="event-edit.publish-btn"
                            >
                                {eventStatus}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.page}>
                <div className="container">
                    <div className="row">
                        {(event && clubDetails) && (
                            <div className="col-12 col-md-6 offset-md-3">
                                {step === 1 && (
                                    <div className={`${styles.section}`}>
                                        <div className={`bg-transparent pl0 pr0 pt32`}>
                                            {club && (
                                            <div className={`text-highlight2 text-teal`}>
                                                <StatsigLink
                                                    gate={featureGates.CLUB_LINKS_REDIRECT}
                                                    enabledTo={`${config.nextAppHost}/${getNewClubPath(event?.club?.tierTypeId)}/${event?.club?.id}`}
                                                    disabledTo={`/clubs/${event?.club?.id}`}
                                                >
                                                    {event?.club?.name}
                                                </StatsigLink>
                                            </div>
                                                )}
                                            <h2 data-testid="event-edit.step1.edit-event-title">Edit Event</h2>
                                        </div>
                                    </div>
                                )}
                                {clubDetails && (
                                    <Form
                                        onSubmit={(values, toStep) => {
                                            handleSubmit({ values }, toStep);
                                        }}
                                        onChange={() => { setEventDataModified(); }}
                                        defaultValues={defaultValues}
                                    >
                                        {(formApi) => {
                                            const { submitForm, values, errors, asyncErrors } = formApi;
                                            exposeFormApi(formApi);
                                            return (
                                                <form onSubmit={submitForm} data-testid="event-edit.wizard.form">
                                                    <EventFacilitiesHydrator
                                                        formApi={formApi}
                                                        hydratedRef={eventFacilitiesHydratedRef}
                                                    />
                                                    <Step
                                                        eventType={values.eventType}
                                                        errors={errors}
                                                        asyncErrors={asyncErrors}
                                                        currentStep={step}
                                                        onSubmit={submitForm}
                                                        showStepHeader={showStepHeader}
                                                        stepTitles={getStepTitles(step, clubDetails)}
                                                        totalSteps={state.totalSteps}
                                                        showPaymentSetup={false}
                                                        formApi={formApi}
                                                        submitting={state.submitting}
                                                        editing={true}
                                                        setTiers={setTiers}
                                                        tiers={tiers}
                                                        values={values}
                                                    >
                                                        <StepFields
                                                            formApi={formApi}
                                                            club={{
                                                                clubId: club?.id,
                                                                clubDetails,
                                                            }}
                                                            defaultValues={defaultValues}
                                                            setTiers={setTiers}
                                                            tiers={tiers}
                                                            remainingTiers={remainingTiers}
                                                            setRemainingTiers={setRemainingTiers}
                                                            currentStep={step}
                                                            showTip={state.tips?.verifiedEvent || true}
                                                            showPaymentSetup={false}
                                                            stripeUrl={stripeUrl}
                                                            paymentProvider={paymentProvider}
                                                            activePaymentsEnabled={activePaymentsEnabled}
                                                            isDemoEvent={state.isDemoEvent}
                                                            openModal={openModal}
                                                            closeCreatePage={closeEditPage}
                                                            applyForVerified={applyForVerified}
                                                            onBack={handleStepBack}
                                                            organizers={state.organizers}
                                                            onEventTypeChange={null}
                                                            onDivisionFeeChange={onDivisionFeeChange}
                                                            onVerifiedChange={onVerifiedChange}
                                                            setOrganizers={setOrganizers}
                                                            PopUpTypes={PopUpTypes}
                                                            editing={true}
                                                            isOwner={isOwner}
                                                            showUpgradeToPerformanceHsPopup={showUpgradeToPerformanceHsPopup}
                                                            isRegionLocked={state.event.regionsLockedOn}
                                                            eventId={eventId}
                                                            isNewEvent={false}
                                                            sportTypeId={event?.sportTypeId}
                                                            hasDraws={!!event?.draws?.length}
                                                            registrationsCount={event?.registrationsCount}
                                                        />
                                                    </Step>
                                                </form>
                                            );
                                        }}
                                    </Form>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <PopupV3
                    title="Error"
                    message={state.error}
                    type={PopupTypes.ERROR}
                    isOpen={!!state.error}
                    onConfirmClick={() => {
                        setState((prevState) => ({
                            ...prevState,
                            error: null,
                            errorPopUp: true,
                        }));
                    }}
                />
                <RecurringSubmissionPopups
                    eventName={event?.name}
                    showSaveDraftPopup={!!state.showRecurringSaveDraftPopup}
                    showPublishPopup={!!state.showRecurringPublishPopup}
                    recurringPublishSelection={state.recurringPublishSelection || []}
                    onConfirmSaveDraft={confirmRecurringSaveDraft}
                    onCancelSaveDraft={closeRecurringSaveDraftPopup}
                    onConfirmPublish={confirmRecurringPublish}
                    onCancelPublish={closeRecurringPublishPopup}
                    onToggleSelectAll={toggleSelectAllRecurringPublishSelection}
                    onToggleEvent={updateRecurringPublishSelection}
                    timeZone={event?.eventSchedule?.timeZone || 77} // 'America/Los_Angeles'
                    isPrimaryPublished={!!state.publish}
                    onTogglePrimary={(checked) => {
                        setState((prevState) => ({
                            ...prevState,
                            publish: checked,
                        }));
                    }}
                    primaryEvent={event}
                />
                {state.showIncompletePopup && (
                    <IncompleteApplicationPopup
                        onSaveClick={() => handleSubmit(formApi.current?.values)}
                        onCloseClick={closePopups}
                        submitting={submitting}
                    />
                )}
                {showCloneEventPopUp && (
                    <EventDuplicatePopup
                        onCtaClick={() => {
                            setState((prevState) => ({
                                ...prevState,
                                showCloneEventPopUp: false,
                            }));
                        }}
                    />
                )}

                {(state.showUpgradeToPerformanceHsPopup) && (
                    <UpgradeToPerformanceHsPopUp
                        onCloseClick={handlePerformanceHsPopupCloseClick}
                        handleUpgradeClick={handlePerformanceHsPopupUpgradeClick}
                        clubOptionsToUpgrade={state.clubToUpgrade}
                    />
                )}
                {state.showProClubPopup && (
                    <UpgradeToProClubPopUp
                        data-testid="modal.event-edit.upgrade-pro-club"
                        skipIntro={true}
                        onCloseClick={() => setState(prevState => ({ ...prevState, showProClubPopup: false }))}
                        onSuccess={handleProClubSuccess}
                        clubId={club?.id}
                        clubTierType={clubDetails.tierType}
                        clubName={clubDetails.name}
                    />
                )}

                <NavigationPrompt
                    renderIfNotActive={false}
                    when={(crntLocation, nextLocation) => unsavedChanges() && (!nextLocation?.pathname?.startsWith(crntLocation?.pathname))}
                >
                    {({ isActive, onCancel, onConfirm }) => {
                        if (isActive) {
                            return (
                                <Modal visible={true} onClickBackdrop={onCancel}>
                                    <div className="modal-header">
                                        <div className="text-medium" data-testid="modal.event-edit.nav-prompt.warning-title">Warning!</div>
                                        <div
                                            className="close-icon"
                                            onClick={onCancel}
                                        >
                                            <i className="utr utr-close" />
                                        </div>
                                    </div>

                                    <div className={`modal-body`} data-testid="modal.event-edit.nav-prompt.body">
                                        <p className="mb24 text-center" data-testid="modal.event-edit.nav-prompt.unsaved-changes-msg">
                                            You have unsaved changes. If you leave before saving, your changes will be lost.
                                        </p>
                                        <p className="mb24 text-center" data-testid="modal.event-edit.nav-prompt.confirm-leave-msg">
                                            Are you sure you want to leave the page?
                                        </p>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <button
                                                    type="button"
                                                    className={`btn btn-xl btn-block btn-primary`}
                                                    onClick={onCancel}
                                                    data-testid="modal.event-edit.nav-prompt.stay-btn"
                                                >
                                                    No
                                                </button>
                                            </div>
                                            <div className="col-md-6">
                                                <button
                                                    type="button"
                                                    className={`btn btn-xl btn-block btn-default`}
                                                    onClick={onConfirm}
                                                    data-testid="modal.event-edit.nav-prompt.leave-btn"
                                                >
                                                    Yes
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                </Modal>
                            );
                        }
                        return null;
                    }}
                </NavigationPrompt>
            </div>
        </div>
    );
}

EventEditPageV2.propTypes = {
    match: routerMatchShape.isRequired,
    history: routerHistoryShape.isRequired,
    location: routerLocationShape.isRequired,
};

export default requireAuth(EventEditPageV2);

// --- Hydration logic for event facilities ---
function EventFacilitiesHydrator({ formApi, hydratedRef }) {
  const { values } = formApi;

  useEffect(() => {
    if (
      hydratedRef.current ||
      values.eventFacilities?.length ||
      !values.locations?.length
    ) {
      return;
    }

    const derivedEventFacilities = [];

    values.locations.forEach((location) => {
      // eslint-disable-next-line no-unused-expressions
      location.facilities?.forEach((facility) => {
        const hasEnabledCourt = facility.courts?.some(
          (court) => court.enabled === true,
        );

        if (hasEnabledCourt) {
          derivedEventFacilities.push({
            clubLocationFacilityId: facility.id,
          });
        }
      });
    });

    if (derivedEventFacilities.length > 0) {
      formApi.setValue('eventFacilities', derivedEventFacilities);
    }

    hydratedRef.current = true;
  }, [values.locations, values.eventFacilities]);

  return null;
}
